import sortBy from "lodash/sortBy";
import orderBy from "lodash/orderBy";
import { format } from "date-fns";

import {
  ONLY_MANUAL_PLATFORMS,
  PLATFORMS,
  PlatformSpec,
} from "../constants/Platforms";
import {
  accountHasGroup,
  groupAccounts,
} from "../entities/accounts/libs/accounts-groups";
import {
  AccountGroup,
  AccountPlatformType,
  AccountType,
  PlatformName,
  platformAccountNameMap,
} from "../shared/types/accounts";
import {
  setLocalStorageHomeSelectedGroupName,
  setLocalStorageSelectedAccounts,
} from "../libs/storage/adapters";
import createStorage from "../libs/storage/storage";
import { InboxLocalStorageKey } from "../types/conversationInbox";
import { PlatformNameToPlatformTypeMap } from "../features/post/format-post";
import { AccountGroupType } from "../features/account-groups-form/accountsGroups.model";

export const BRAND_DELIMITER = "->";

const getPlatformSpec = (platformType: string): PlatformSpec => {
  return (
    (PLATFORMS as Record<string, PlatformSpec>)[platformType] ?? PLATFORMS.IG
  );
};

// NOTE: (Andrey) getAccountName has used by diff 'account' types
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getAccountName = (account: {
  loginAlias?: string;
  login: string;
}): string => {
  return account.loginAlias ? account.loginAlias : account.login;
};

export const getAccountNameParts = (account: {
  loginAlias?: string;
  login: string;
}): [string | null, string] => {
  const name = getAccountName(account);

  // Safety check: handle undefined/null name
  if (!name) {
    return [null, ""];
  }

  const parts = name.split(BRAND_DELIMITER);

  if (parts.length !== 2) {
    return [null, name.trim()];
  }

  return [parts[0].trim(), parts[1].trim()];
};

export const sortAccounts = (accounts: AccountType[]): AccountType[] => {
  return sortBy(accounts, [
    (a) => {
      const username = a?.loginAlias ? a?.loginAlias : a?.login;

      return (username || "").toLowerCase();
    },
  ]);
};

export const getPlatformName = (platformType: string): PlatformName =>
  getPlatformSpec(platformType).NAME;

export const getPlatformLabel = (platformType: string): string =>
  getPlatformSpec(platformType).LABEL;

export const findManualPlatform = (
  accounts: AccountType[],
  platformType: AccountPlatformType
) =>
  accounts?.find(
    (account) => account.platformType === platformType && !account.platformId
  );

export const findTiktokAutoPosting = (accounts: AccountType[]) =>
  accounts?.find(
    (platform) =>
      platform.platformType === PlatformNameToPlatformTypeMap.tiktok &&
      platform.platformId
  );

// used for talking about the current POST itself
export const getStoryLabel = function (
  type: string,
  platforms: any[],
  currentPlatformType: string,
  accounts: AccountType[]
): string {
  const hasManualTTPlatform = findManualPlatform(accounts || [], "TT");
  const hasManualTHPlatform = findManualPlatform(accounts || [], "TH");

  const hasNotification =
    hasManualTTPlatform ||
    hasManualTHPlatform ||
    platforms.some((platform: { TYPE: AccountPlatformType }) =>
      ONLY_MANUAL_PLATFORMS.includes(platform.TYPE)
    );
  if (currentPlatformType === "TT") {
    return "TikTok post";
  } else if (currentPlatformType === "SC") {
    return "Snap";
  } else if (currentPlatformType === "TH") {
    return "Threads post";
  } else if (currentPlatformType === "OT" || hasNotification) {
    return "post";
  }
  switch (type) {
    case "igtv":
      return "IGTV";
    case "reel":
      return "Reel";
    case "story":
    default:
      return "story";
  }
};

// used when talking about the current ACCOUNT(S) for the post
export const getStoryAccountLabel = function (
  type: string,
  platforms: any[],
  currentPlatformType: string
): string {
  const hasNotification = platforms.some(
    (platform: { TYPE: AccountPlatformType }) =>
      ONLY_MANUAL_PLATFORMS.includes(platform.TYPE)
  );
  if (currentPlatformType === "TT") {
    return "TikTok";
  } else if (currentPlatformType === "SC") {
    return "Snapchat";
  } else if (currentPlatformType === "TH") {
    return "Threads";
  } else if (currentPlatformType === "OT" || hasNotification) {
    return "notification account";
  }
  switch (type) {
    case "igtv":
      return "IGTV";
    case "reel":
      return "Instagram Reel";
    case "story":
    default:
      return "story";
  }
};

// TODO now it runs a lot times, need run it once when get data and store it, maybe a selector `getAccountsByGroup`
export const splitAccounts = (
  accounts: AccountType[],
  accountsGroups?: AccountGroupType[]
): { groupedAccounts: AccountGroup[]; otherAccounts: AccountType[] } => {
  // eslint-disable-next-line unicorn/no-array-callback-reference
  const groupedAccounts = groupAccounts(accounts.filter(accountHasGroup));
  const emptyGroups = accountsGroups
    ? accountsGroups.filter((group) => group.accountIds.length === 0)
    : [];
  const otherAccounts = sortAccounts(
    accounts.filter((account: AccountType) => !accountHasGroup(account))
  );

  return {
    groupedAccounts: [
      ...groupedAccounts,
      ...emptyGroups.map((group) => ({
        title: group.name,
        groupId: group._id,
        accounts: [],
      })),
    ].sort((groupA, groupB) =>
      groupA.title.toLowerCase().localeCompare(groupB.title.toLowerCase())
    ),
    otherAccounts,
  };
};

export const getPlatforms = (accounts: AccountType[]): PlatformSpec[] => {
  const platforms: PlatformSpec[] = [];
  const platformTypes: AccountPlatformType[] = [];
  accounts.forEach((account) => {
    const platformType: AccountPlatformType = account.platformType || "IG";
    if (!platformTypes.includes(platformType)) {
      platformTypes.push(platformType);
      platforms.push(PLATFORMS[platformType]);
    }
  });

  return orderBy(platforms, ["ORDER"], ["asc"]);
};

export const getFormattedDate = (
  timePreference: string,
  date: string | number | Date
): string => {
  const dateObj = new Date(date);
  const formatStr =
    timePreference === "12h"
      ? "EEE d MMM yyyy h:mm aaa"
      : "EEE d MMM yyyy H:mm";
  return format(dateObj, formatStr);
};

export const hasTwitter = (accounts: AccountType[]): boolean =>
  accounts.some((account) => account.platformType === "TW");

export const filterAccountsByText =
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  (searchText: string | undefined) => (account: any) => {
    if (!account) return false;

    if (!searchText) return true;

    return getAccountName(account)
      .toLowerCase()
      .includes(searchText.trim().toLowerCase());
  };

export const filterAccountsByPlatformType =
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  (platformType: string | undefined) => (account: any) => {
    if (!account) return false;

    if (!platformType) return true;

    let accountPlatformType = account.platformType;
    // NOTE: (Andrey) some time we have account without platformType or platformName, by default this is IG account
    if (!account.platformType && !account.platformName) {
      accountPlatformType = "IG";
    }

    return accountPlatformType
      ? accountPlatformType === platformType
      : platformType in PLATFORMS &&
          account.platformName === getPlatformName(platformType);
  };

const cleanUpStorage = () => {
  // Note: GA4 attribution keys (e.g. `sked_ga_user_id`) are cleared by
  // `teardownAnalyticsIdentity()` in services/analytics-init.ts, invoked
  // alongside `teardownHubSpotChat()` from the dynamic-import logout path
  // below. Don't add them here — the analytics chunk owns its own teardown
  // so this util doesn't pull Amplitude / axios into every chunk.
  const localStorageWrapper = createStorage(window.localStorage);
  [
    InboxLocalStorageKey.selectedAccounts,
    "isConfirmPostCancel",
    "isConfirmPostDelete",
    "isConfirmDraftDelete",
    "libraryFilter",
    "selectedPostContentTypes",
    "accountFilter",
    "HomeSelectedGroupName",
    "postFormDrafts",
  ].forEach((key: string) => localStorageWrapper.removeItem(key));
};

// Hard cap on how long we'll wait for the HubSpot teardown chunk to load
// before navigating to /logout regardless. Without this, a stalled or rejected
// dynamic import (offline, blocked by an ad-blocker, slow CDN) can delay
// navigation long enough
// that the user re-clicks Log out or assumes the action failed, while their
// session cookie remains valid.
const LOGOUT_TEARDOWN_WATCHDOG_MS = 2000;

export const logout = (): void => {
  // Clear HubSpot chat identity BEFORE the nav so the next visitor on this
  // machine doesn't inherit the previous user's verified chat context.
  // `window.location.href` would kill the JS state anyway, but HubSpot's own
  // cookies + conversation history only reset when we explicitly call
  // `HubSpotConversations.clear()`.
  //
  // Dynamic import so `utils/accounts` (imported across the codebase) does
  // NOT pull the full analytics-init module — plus Amplitude plugins, axios,
  // etc. — into every chunk that imports account utilities.
  cleanUpStorage();
  setLocalStorageSelectedAccounts("[]");
  setLocalStorageHomeSelectedGroupName("");

  let navigated = false;
  const navigate = () => {
    if (navigated) return;
    navigated = true;
    clearTimeout(watchdog);
    window.location.href = "/logout";
  };

  // Watchdog: navigate after the cap regardless of teardown state. Teardown
  // will still complete in the background if/when the chunk eventually
  // resolves, but the user is already on /logout. Cancelled on the
  // happy path so the timer doesn't linger as a (harmless) no-op.
  const watchdog = setTimeout(navigate, LOGOUT_TEARDOWN_WATCHDOG_MS);

  void import("../services/analytics-init")
    .then(({ teardownHubSpotChat, teardownAnalyticsIdentity }) => {
      // GA4 identity teardown is synchronous + idempotent; run it alongside
      // the HubSpot teardown so the localStorage user_id cache + live gtag
      // user_id are cleared before navigation.
      teardownAnalyticsIdentity();
      // SKE-3678: surface logout via the stable `user_signed_out` event.
      // Downstream container/workspace-specific handling (including the
      // PostHog reset that prevents a later login in the same browser from
      // inheriting the prior identified user) is configured outside this file.
      window.dataLayer?.push({ event: "user_signed_out" });
      return teardownHubSpotChat();
    })
    .catch((e) =>
      console.warn("[logout] analytics teardown failed (non-fatal)", e)
    )
    .finally(navigate);
};

export const normalizeAccounts = (accounts: any) => {
  return accounts.map((account: any) => {
    const normalizedAccount = {
      ...account,
      platformName: getPlatformName(account.platformType),
    };

    if (
      account.platformType === PLATFORMS.PI.TYPE &&
      !account.piV5 &&
      normalizedAccount.status !== "failed"
    ) {
      normalizedAccount.status = "failed";
      normalizedAccount.failReason =
        "Pinterest upgrade requires you to reconnect your account. Please re-authenticate by 30 June 2023 or notify your account Administrator re-authenticate your Pinterest account.";
      normalizedAccount.userDisplayReason =
        "Pinterest upgrade requires you to reconnect your account. Please re-authenticate by 30 June 2023 or notify your account Administrator re-authenticate your Pinterest account.";
      normalizedAccount.notifiedApiDisconnect = new Date();
      normalizedAccount.failedAt = new Date();
    }

    return normalizedAccount;
  });
};

export const globalUserPreferencesToggles = {
  enableAdPosts: false,
  enableBoostPosts: false,
};

export const selectAmountGroupAccountsIds = (
  accounts: AccountGroup[],
  amount = 3
): string[] => {
  const accountsAmount = accounts.slice(0, amount);
  const accountsFromGroup = accountsAmount
    .map((_accounts) => _accounts.accounts)
    .flat();
  return accountsFromGroup.map((el) => el._id);
};

export const selectAmountOfAccountsIds = (
  accounts: AccountType[],
  amount: number
): string[] => {
  return accounts.slice(0, amount).map((el) => el._id);
};

export const getFirstGroupOrAmountOfAccountsIds = (
  accounts: AccountType[],
  amountAccountsWithoutGroup = 5,
  amountGroup = 3
): string[] => {
  const { groupedAccounts } = splitAccounts(accounts);

  if (groupedAccounts.length === 0) {
    return selectAmountOfAccountsIds(accounts, amountAccountsWithoutGroup);
  } else if (groupedAccounts.length > 3) {
    return selectAmountGroupAccountsIds(groupedAccounts, 1);
  }

  return selectAmountGroupAccountsIds(groupedAccounts, amountGroup);
};

export const getPlatformNameType = (
  account?: AccountType,
  fullName?: string
) => {
  let platformName = fullName || account?.loginAlias || account?.login;
  let platformType = account?.platformType || "";

  const hasPlatform = Boolean(account?.platformId);
  const isTikTok =
    account?.platformType === PlatformNameToPlatformTypeMap.tiktok;

  const isThreads =
    account?.platformType === PlatformNameToPlatformTypeMap.threads;

  if (isTikTok && !hasPlatform) {
    platformName = `TikTok: ${platformName}`;
    platformType = PlatformNameToPlatformTypeMap.other as PlatformName;
  }
  if (isThreads && !hasPlatform) {
    platformName = `Threads: ${platformName}`;
    platformType = PlatformNameToPlatformTypeMap.other as PlatformName;
  }

  return {
    platformType,
    platformName,
  };
};

export const resolveAccountPlatformType = (
  platformType?: string,
  platformName?: string
): AccountPlatformType | undefined => {
  const normalizedPlatformType = platformType?.trim().toUpperCase();
  if (
    normalizedPlatformType &&
    normalizedPlatformType in platformAccountNameMap
  ) {
    return normalizedPlatformType as AccountPlatformType;
  }

  if (!platformName) {
    return undefined;
  }

  // Fallback to platformName for legacy or display-only payloads that may
  // have missing/invalid platformType while still carrying a valid platform name.
  const normalizedPlatformName = platformName.toLowerCase();
  if (
    normalizedPlatformName === "play" ||
    normalizedPlatformName === "youtuberedplay"
  ) {
    return "YT";
  }

  return PlatformNameToPlatformTypeMap[
    normalizedPlatformName as keyof typeof PlatformNameToPlatformTypeMap
  ] as AccountPlatformType | undefined;
};
